import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Avatar,
  Stack,
  useTheme
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';

import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GridOnIcon from '@mui/icons-material/GridOn';
import ApiIcon from '@mui/icons-material/Api';
import GppGoodIcon from '@mui/icons-material/GppGood';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PolicyIcon from '@mui/icons-material/Policy';
import BugReportIcon from '@mui/icons-material/BugReport';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BrandLogo from '../components/Brand/BrandLogo';
import PublicFooter from '../components/Public/PublicFooter';
import Screenshot, { ScreenshotGrid } from '../components/Public/Screenshot';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import ExploreIcon from '@mui/icons-material/Explore';
import PublicIcon from '@mui/icons-material/Public';
import DnsIcon from '@mui/icons-material/Dns';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HubIcon from '@mui/icons-material/Hub';
import DescriptionIcon from '@mui/icons-material/Description';
import ForumIcon from '@mui/icons-material/Forum';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SummarizeIcon from '@mui/icons-material/Summarize';
import RuleIcon from '@mui/icons-material/Rule';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TimerIcon from '@mui/icons-material/Timer';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LoginIcon from '@mui/icons-material/Login';
import HistoryIcon from '@mui/icons-material/History';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import StorageIcon from '@mui/icons-material/Storage';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

// ---------------------------------------------------------------------------
// Reusable presentational bits
// ---------------------------------------------------------------------------

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
    {eyebrow && (
      <Typography
        variant="overline"
        color="secondary.main"
        sx={{ fontWeight: 700, letterSpacing: 1.5 }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="h6"
        sx={{ color: 'text.secondary', maxWidth: 720, mx: 'auto', fontWeight: 400 }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const FeatureCard = ({ icon, title, description }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          width: 48,
          height: 48,
          mb: 2
        }}
      >
        {icon}
      </Avatar>
      <Typography variant="subtitle1" component="h3" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const HowItWorksStep = ({ index, title, description }) => (
  <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', height: '100%' }}>
    <Avatar sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', width: 44, height: 44, fontWeight: 'bold' }}>
      {index}
    </Avatar>
    <Typography variant="subtitle1" fontWeight="bold">
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Stack>
);

// ---------------------------------------------------------------------------
// Content data
// ---------------------------------------------------------------------------

const heroChips = [
  { label: 'Any alert source', icon: <ApiIcon fontSize="small" /> },
  { label: 'AI triage & correlation', icon: <AutoAwesomeIcon fontSize="small" /> },
  { label: 'MITRE ATT&CK', icon: <GridOnIcon fontSize="small" /> },
  { label: 'Multi-tenant', icon: <ApartmentIcon fontSize="small" /> }
];

const howItWorks = [
  {
    title: 'Send alerts from anywhere',
    description: 'Native EDR connectors, SIEM and log forwarders, or a single POST to the intake API. Raw JSON and log lines are fine — no schema mapping required.'
  },
  {
    title: 'AI reads and triages every alert',
    description: 'Gemini parses unstructured payloads into structured detections, extracts hosts, users and indicators, classifies and scores severity, and enriches with threat intel.'
  },
  {
    title: 'AI correlates alerts into cases',
    description: 'Related detections across hosts, users, indicators and time are grouped into one case with a reconstructed attack timeline — not a pile of duplicates.'
  },
  {
    title: 'Respond, report and improve',
    description: 'Playbooks, automation rules and SLAs drive the response. AI writes the incident report, and MTTA/MTTR and ATT&CK coverage keep tightening the loop.'
  }
];

const inActionShots = [
  {
    src: '/screens/ai-analysis.webp',
    title: 'AI triage',
    alt: 'AI Analysis card for a critical Log4Shell detection, showing classification, severity reasoning and 95% confidence',
    caption: 'Every alert is classified, scored and explained the moment it lands — here a Log4Shell exploit at 95% confidence.'
  },
  {
    src: '/screens/attack-timeline.webp',
    title: 'Attack timeline',
    alt: 'Attack timeline with a "Confirmed intrusion, confidence 98%" verdict followed by per-event process, command line, hash and network details',
    caption: 'Correlated detections become one chronological story, with command lines, hashes and network activity per step.'
  },
  {
    src: '/screens/ai-report.webp',
    title: 'AI incident report',
    alt: 'AI-generated incident report with an executive summary and a chronological technical narrative',
    caption: 'An executive summary and technical narrative written for every case, ready to copy into your ticket or handover.'
  },
  {
    src: '/screens/threat-intel.webp',
    title: 'Threat intelligence verdicts',
    alt: 'Threat Intelligence card showing a Malicious verdict with an AI summary of results across providers',
    caption: 'Indicators checked against multiple providers, with one plain-language verdict and the reasoning behind it.'
  },
  {
    src: '/screens/mitre-heatmap.webp',
    title: 'MITRE ATT&CK coverage',
    alt: 'MITRE ATT&CK heatmap showing detection activity by tactic and technique',
    caption: 'See which tactics and techniques are firing across the tenant, at a glance.',
    position: 'left top'
  },
  {
    src: '/screens/needs-attention.webp',
    title: 'Needs attention',
    alt: 'Needs attention list of cases that have breached or are about to breach their SLA',
    caption: 'Cases that have breached or are about to breach their SLA float to the top, so nothing slips.'
  }
];

const integrations = [
  {
    icon: <SecurityIcon />,
    name: 'CrowdStrike Falcon',
    description: 'Alert ingestion, endpoint inventory, one-click network isolation, indicator lookups, and status sync.'
  },
  {
    icon: <ShieldIcon />,
    name: 'SentinelOne',
    description: 'Alert ingestion, endpoint inventory, one-click network isolation, indicator lookups, and status sync.'
  },
  {
    icon: <GppGoodIcon />,
    name: 'Microsoft Defender for Endpoint',
    description: 'Alert ingestion, endpoint inventory, one-click network isolation, indicator lookups, and status sync.'
  },
  {
    icon: <VerifiedUserIcon />,
    name: 'Palo Alto Cortex XDR',
    description: 'Alert ingestion, endpoint inventory, one-click network isolation, and alert status sync back to the vendor.'
  },
  {
    icon: <PolicyIcon />,
    name: 'Sophos Central',
    description: 'Alert ingestion, endpoint inventory, one-click network isolation, and alert status sync back to the vendor.'
  },
  {
    icon: <HubIcon />,
    name: 'SIEM, email, cloud & network',
    description: 'Splunk, Elastic, Sentinel, email security, cloud audit logs, firewalls and IDS/IPS — forward alerts as they are and let AI structure them.'
  },
  {
    icon: <ApiIcon />,
    name: 'Alert intake API',
    description: 'Post any JSON or log line with an API key. If it is not in Excubia’s format, AI works out the structure and extracts the indicators.'
  },
  {
    icon: <TrackChangesIcon />,
    name: 'Threat-intel feeds',
    description: 'Pull text, CSV, STIX or TAXII feeds on a schedule so your own intelligence matches against every alert.'
  }
];

const threatIntelSources = [
  { icon: <BugReportIcon />, name: 'VirusTotal', description: 'File, URL, and IP reputation verdicts from dozens of engines.' },
  { icon: <ReportProblemIcon />, name: 'AbuseIPDB', description: 'Abuse confidence scores for reported malicious IPs.' },
  { icon: <TrackChangesIcon />, name: 'AlienVault OTX', description: 'Community threat-intelligence pulses and indicator context.' },
  { icon: <GppMaybeIcon />, name: 'abuse.ch URLhaus / MalwareBazaar', description: 'Malicious URL and malware sample feeds.' },
  { icon: <BlurOnIcon />, name: 'GreyNoise', description: 'Internet-wide scanner and noise classification for IPs.' },
  { icon: <ExploreIcon />, name: 'Shodan', description: 'Exposed service and banner lookups for hosts and IPs.' },
  { icon: <PublicIcon />, name: 'GeoIP', description: 'Geolocation context for indicators and source addresses.' },
  { icon: <DnsIcon />, name: 'DNS', description: 'Forward and reverse resolution for domains and IPs.' }
];

const aiCapabilities = [
  { icon: <AutoAwesomeIcon />, title: 'AI alert parsing', description: 'Vendor-specific or unstructured payloads become structured detections with hosts, users, processes and indicators extracted automatically.' },
  { icon: <SmartToyIcon />, title: 'Triage & classification', description: 'Every alert is scored, categorised and given a severity on arrival, so analysts start with signal, not noise.' },
  { icon: <HubIcon />, title: 'AI case correlation', description: 'Groups related detections across hosts, users, indicators and time into a single case, and surfaces links analysts would miss.' },
  { icon: <HistoryIcon />, title: 'Attack timeline reconstruction', description: 'Rebuilds the sequence of malicious activity — commands, processes, files and network — into a detailed incident timeline.' },
  { icon: <DescriptionIcon />, title: 'AI incident reports', description: 'Generates an executive summary, technical narrative, and next steps for every case.' },
  { icon: <ForumIcon />, title: '"Ask AI about this record"', description: 'A grounded assistant that answers questions about the detection, entity, or case in front of you.' },
  { icon: <LightbulbIcon />, title: 'Playbook suggestions', description: 'Recommends the response playbook that best fits the detection type.' },
  { icon: <SummarizeIcon />, title: 'Threat-intel summaries', description: 'Condenses enrichment results into a plain-language summary analysts can act on.' }
];

const automationFeatures = [
  { icon: <RuleIcon />, title: 'Automation rules', description: 'Conditions trigger actions: set severity or status, tag, assign, create a case with a playbook, suppress, or notify.' },
  { icon: <AssignmentTurnedInIcon />, title: 'Response playbooks', description: 'Reusable playbooks with task checklists guide analysts through consistent response steps.' },
  { icon: <TimerIcon />, title: 'SLA targets & breach monitoring', description: 'Per-severity SLA targets with proactive breach monitoring keep response times honest.' },
  { icon: <AccountTreeIcon />, title: 'Entity auto-linking', description: 'Hosts and users are auto-linked across detections with decayed risk scores that reflect recent activity.' }
];

const visibilityFeatures = [
  { icon: <GridOnIcon />, title: 'MITRE ATT&CK heatmap', description: 'Visualize coverage and activity across the ATT&CK matrix at a glance.' },
  { icon: <TrendingUpIcon />, title: 'Reports', description: 'MTTA/MTTR, SLA compliance, trends, top hosts/users/techniques, and analyst load.' },
  { icon: <SearchIcon />, title: 'Global search', description: 'Find any detection, case, entity, or indicator instantly across the tenant.' },
  { icon: <FileDownloadIcon />, title: 'CSV exports', description: 'Export detections, cases, and reports for offline analysis or audits.' },
  { icon: <EmailIcon />, title: 'Daily email digest', description: 'A daily rollup of what happened so nothing slips through overnight.' },
  { icon: <NotificationsActiveIcon />, title: 'Notifications', description: 'In-app alerts plus Slack and Teams notifications keep teams in the loop.' }
];

const platformFeatures = [
  { icon: <ApartmentIcon />, title: 'Tenant-scoped data', description: 'Each organization’s data lives in its own partition, every request is bound to that tenant by a signed identity, and clients never talk to the database directly.' },
  { icon: <AdminPanelSettingsIcon />, title: 'Role-based access', description: 'Custom roles give teams precise control over who can see and do what.' },
  { icon: <LoginIcon />, title: 'Google & email sign-in', description: 'Flexible authentication that fits how your team already works.' },
  { icon: <HistoryIcon />, title: 'Audit log', description: 'Every change is recorded in a complete, searchable audit trail.' },
  { icon: <VpnKeyIcon />, title: 'Encrypted credentials', description: 'Integration API keys and secrets are encrypted at rest.' },
  { icon: <StorageIcon />, title: 'Data retention policies', description: 'Configurable retention keeps data lifecycle aligned with your requirements.' },
  { icon: <SupervisorAccountIcon />, title: 'Platform-admin area', description: 'A separate admin area for managing tenants outside day-to-day case work.' }
];

const plans = [
  {
    name: 'Trial',
    price: 'Free',
    period: '60 days',
    description: 'Perfect for evaluation',
    features: [
      '5 team members',
      '100 detections/month',
      '50 cases/month',
      '1GB storage',
      'Email support'
    ],
    popular: false
  },
  {
    name: 'Starter',
    price: '$199',
    period: 'per month',
    description: 'Great for small teams',
    features: [
      '10 team members',
      '1,000 detections/month',
      '500 cases/month',
      '10GB storage',
      'Priority support'
    ],
    popular: false
  },
  {
    name: 'Professional',
    price: '$499',
    period: 'per month',
    description: 'Ideal for growing organizations',
    features: [
      '50 team members',
      '10,000 detections/month',
      '5,000 cases/month',
      '100GB storage',
      '24/7 support',
      'Advanced analytics'
    ],
    popular: true
  },
  {
    name: 'Enterprise',
    price: '$1,499',
    period: 'per month',
    description: 'For large enterprises',
    features: [
      'Unlimited team members',
      'Unlimited detections',
      'Unlimited cases',
      '1TB storage',
      'Dedicated support',
      'Custom integrations',
      'SLA guarantee'
    ],
    popular: false
  }
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const altSectionBg = theme.palette.mode === 'dark' ? grey[900] : grey[50];

  const featureSections = [
    {
      key: 'ai',
      eyebrow: 'AI alert processing & correlation · Google Gemini',
      title: 'AI that processes every alert and connects the dots',
      subtitle: 'Each alert is parsed, classified and scored the moment it arrives. Related alerts are correlated into one case with a reconstructed attack timeline, and the report writes itself.',
      shot: {
        src: '/screens/attack-timeline.webp',
        frame: 'card',
        maxHeight: 540,
        alt: 'Attack timeline reconstructed by AI: a "Confirmed intrusion, confidence 98%" verdict followed by each event with process, command line, file hashes and network connections',
        caption: 'AI reconstructs the attack from correlated detections: a confirmed-intrusion verdict, then every step with its process, command line, hashes and network activity.'
      },
      secondary: {
        src: '/screens/ask-ai.webp',
        maxWidth: 340,
        alt: 'Ask AI about this record panel with suggested questions about the current case',
        caption: 'Ask AI about any detection, entity or case — answers are grounded in the record.'
      },
      items: aiCapabilities,
      columns: { xs: 12, sm: 6, md: 4 }
    },
    {
      key: 'integrations',
      eyebrow: 'Any alert source',
      title: 'Bring every alert, not just EDR',
      subtitle: 'Native EDR connectors add endpoint inventory, isolation and status sync. Everything else — SIEM, email security, cloud, firewalls, IDS, scripts — arrives through the alert intake API in whatever shape it comes.',
      shot: {
        src: '/screens/integrations.webp',
        frame: 'browser',
        alt: 'Integrations page with five connected EDR agents — Sophos, Palo Alto Cortex XDR, Microsoft Defender, SentinelOne and CrowdStrike — each with capability chips',
        caption: 'Five native EDR connectors, all connected and healthy, each showing what it can do: alerts, endpoint inventory, isolation and status sync.'
      },
      items: integrations,
      columns: { xs: 12, sm: 6, md: 4 }
    },
    {
      key: 'threat-intel',
      eyebrow: 'Threat-intelligence enrichment',
      title: 'Every indicator, automatically enriched',
      subtitle: 'Detections and indicators are enriched with verdicts and scores, cached for 24 hours, and escalate severity automatically when malicious.',
      shot: {
        src: '/screens/threat-intel.webp',
        frame: 'card',
        maxHeight: 540,
        alt: 'Threat Intelligence card with a Malicious verdict and an AI summary of results from several providers',
        caption: 'Results from every provider are combined into one verdict, with an AI summary of why — including where providers disagree.'
      },
      items: threatIntelSources,
      columns: { xs: 12, sm: 6, md: 3 }
    },
    {
      key: 'automation',
      eyebrow: 'Automation & workflow',
      title: 'Consistent response, every time',
      subtitle: 'Codify how your SOC responds so nothing depends on who is on shift.',
      shot: {
        src: '/screens/automation.webp',
        frame: 'browser',
        alt: 'Automation rules settings page explaining how conditions trigger actions such as setting severity, assigning, tagging, creating a case or notifying',
        caption: 'Automation rules: when a detection matches your conditions, set severity or status, assign, tag, create a case with a playbook, suppress or notify.'
      },
      secondary: {
        src: '/screens/sla.webp',
        maxWidth: 300,
        alt: 'SLA card on a case showing response and resolution targets and time remaining',
        caption: 'Per-severity response and resolution targets on every case.'
      },
      items: automationFeatures,
      columns: { xs: 12, sm: 6, md: 3 }
    },
    {
      key: 'visibility',
      eyebrow: 'Visibility & reporting',
      title: 'Know what’s happening — and prove it',
      subtitle: 'From ATT&CK coverage to SLA compliance, the data you need is always one click away.',
      shot: {
        src: '/screens/mitre-heatmap.webp',
        frame: 'card',
        alt: 'MITRE ATT&CK coverage heatmap with detection counts by tactic and technique',
        caption: 'MITRE ATT&CK heatmap: which tactics and techniques are active across your tenant.'
      },
      secondary: {
        src: '/screens/daily-severity.webp',
        maxWidth: 420,
        alt: 'Bar chart of daily detections stacked by severity',
        caption: 'Daily detections by severity, from the Reports page.'
      },
      items: visibilityFeatures,
      columns: { xs: 12, sm: 6, md: 4 }
    },
    {
      key: 'platform',
      eyebrow: 'Platform & security',
      title: 'Built for the SOC, secured for the enterprise',
      subtitle: 'Multi-tenant architecture with the access controls and audit trail security teams expect.',
      shot: {
        src: '/screens/backups.webp',
        frame: 'browser',
        alt: 'Backups and restore settings page where administrators create and restore tenant backups themselves',
        caption: 'Self-service backups and restore, scoped to your tenant.'
      },
      items: platformFeatures,
      columns: { xs: 12, sm: 6, md: 3 }
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Partner banner */}
      <Box
        role="region"
        aria-label="Partner with Excubia"
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? 'warning.dark' : 'warning.light',
          color: theme.palette.mode === 'dark' ? 'warning.contrastText' : 'grey.900',
          py: 1.25
        }}
      >
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <HandshakeIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>Looking for a design partner.</Box>{' '}
                We want to work with a security team or MSSP to build out integrations and shape the product together.
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="contained"
              color="inherit"
              onClick={() => navigate('/partner')}
              sx={{ whiteSpace: 'nowrap', bgcolor: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.900', color: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white', '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'grey.300' : 'grey.800' } }}
            >
              Get in touch
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2, display: { xs: 'block', sm: 'none' } }}><BrandLogo height={64} onDark /></Box>
              <Box sx={{ mb: 2, display: { xs: 'none', sm: 'block' } }}><BrandLogo height={96} onDark /></Box>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' }, color: theme.palette.warning.light }}>
                the SOC&apos;s night watch
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
                Send Excubia alerts from anywhere — EDR, SIEM, email security, cloud, firewalls, or
                your own scripts. AI reads and triages every alert, correlates related activity into a
                single case, and writes the incident report so your analysts act fast.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    '&:hover': { bgcolor: theme.palette.secondary.dark },
                    px: 4,
                    py: 1.5
                  }}
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/register')}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    px: 4,
                    py: 1.5
                  }}
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                <Button
                  variant="text"
                  size="large"
                  startIcon={<PlayCircleOutlineIcon />}
                  sx={{ color: 'white', px: 2, py: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  onClick={() => navigate('/product')}
                >
                  Product tour
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {heroChips.map((chip) => (
                  <Chip
                    key={chip.label}
                    icon={chip.icon}
                    label={chip.label}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      '& .MuiChip-icon': { color: 'white' },
                      mb: 1
                    }}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Box
                sx={{
                  position: 'relative',
                  width: { sm: '100%', md: '118%' },
                  perspective: '1800px'
                }}
              >
                <Box
                  sx={{
                    transform: { md: 'rotateY(-9deg) rotateX(3deg)' },
                    transformOrigin: 'left center',
                    transition: 'transform 0.4s ease',
                    '&:hover': { transform: { md: 'rotateY(-3deg) rotateX(1deg)' } },
                    filter: 'drop-shadow(0 30px 50px rgba(0,0,0,0.45))'
                  }}
                >
                  <Screenshot
                    src="/screens/dashboard.webp"
                    alt="Excubia SOC dashboard showing open and unassigned detections, open cases, SLA breaches, automation and integration health, a needs-attention list of at-risk cases, and recent critical and high detections"
                    caption="The SOC dashboard: open work, SLA breaches, cases that need attention and the latest critical detections in one view."
                    frame="browser"
                    eager
                  />
                </Box>
                <Box
                  component="img"
                  src="/logo192.png"
                  alt=""
                  aria-hidden="true"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    left: -28,
                    bottom: -28,
                    width: 72,
                    height: 72,
                    filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.4))'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How it works */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4}>
          {howItWorks.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={step.title}>
              <HowItWorksStep index={index + 1} title={step.title} description={step.description} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* See it in action */}
      <Box sx={{ bgcolor: altSectionBg, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="From first alert to finished report"
            title="See it in action"
            subtitle="Real screens from Excubia — from the first alert to the finished incident report. Click any screenshot to enlarge it."
          />
          <ScreenshotGrid items={inActionShots} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 5, fontStyle: 'italic' }}>
            Screenshots from a live Excubia tenant fed by simulated EDR telemetry for a fictional organisation.
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button variant="outlined" color="primary" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/product')}>
              Take the full product tour
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Feature sections */}
      {featureSections.map((section, sectionIndex) => (
        <Box
          key={section.key}
          sx={{
            bgcolor: sectionIndex % 2 === 0 ? 'background.default' : altSectionBg,
            py: { xs: 8, md: 12 }
          }}
        >
          <Container maxWidth="lg">
            {section.shot ? (
              <Grid
                container
                spacing={{ xs: 4, md: 6 }}
                alignItems="center"
                direction={{ xs: 'row', md: sectionIndex % 2 === 0 ? 'row' : 'row-reverse' }}
                sx={{ mb: { xs: 5, md: 7 } }}
              >
                <Grid item xs={12} md={5}>
                  <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
                    {section.eyebrow}
                  </Typography>
                  <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
                    {section.title}
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    {section.subtitle}
                  </Typography>
                  {section.secondary && (
                    <Box sx={{ mt: 4, maxWidth: section.secondary.maxWidth || 380 }}>
                      <Screenshot frame="card" showCaption {...section.secondary} />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={7}>
                  <Box sx={{ maxWidth: section.shot.maxWidth || 'none', mx: 'auto' }}>
                    <Screenshot {...section.shot} />
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <SectionHeading eyebrow={section.eyebrow} title={section.title} subtitle={section.subtitle} />
            )}
            <Grid container spacing={3}>
              {section.items.map((item) => (
                <Grid item {...section.columns} key={item.name || item.title}>
                  <FeatureCard
                    icon={item.icon}
                    title={item.name || item.title}
                    description={item.description}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ))}

      {/* Pricing Section */}
      <Box sx={{ bgcolor: featureSections.length % 2 === 0 ? 'background.default' : altSectionBg, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Pricing"
            title="Choose Your Plan"
            subtitle="Start with a free trial, then choose the plan that fits your organization."
          />

          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} lg={3} key={plan.name}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: plan.popular ? `2px solid ${theme.palette.secondary.main}` : '1px solid',
                    borderColor: plan.popular ? theme.palette.secondary.main : 'divider'
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      color="secondary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)'
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h3" component="div" fontWeight="bold" color="primary">
                        {plan.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plan.period}
                      </Typography>
                    </Box>
                    <List dense>
                      {plan.features.map((feature) => (
                        <ListItem key={feature} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              color="secondary"
              sx={{ px: 6, py: 2 }}
              onClick={() => navigate('/register')}
            >
              Start Your Free Trial Today
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white'
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            Ready to Strengthen Your Security Posture?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join the organizations already using Excubia to investigate faster and document everything.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: theme.palette.secondary.main,
                '&:hover': { bgcolor: theme.palette.secondary.dark },
                px: 4,
                py: 1.5
              }}
              onClick={() => navigate('/register')}
            >
              Get Started Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 4,
                py: 1.5
              }}
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      </Container>
      <PublicFooter />
    </Box>
  );
};

export default Home;
